import React, { useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import FeatherIcon from 'feather-icons-react'
import CustomModal, { CustomModalActions } from 'src/components/configuracion/components/customModal'
import ScfoButton from 'src/components/shared/ScfoButton'
import { UserContext } from 'src/context'
import { Roles } from 'src/utils/roles'
import { UnlockStatementsBlockingUnmatch } from 'src/graphql/statements'
import { lockedStatementIdsFromSummary } from 'src/components/tarjetasCredito/creditCardsRoutes'
import { dt } from 'src/utils/documentsI18n'
import 'src/components/documentostributarios/ModalDeleteObligations.scss'

const participantLabel = (item) => [item?.cardLabel, item?.periodKey].filter(Boolean).join(' — ')

export default function StatementLinkedUnmatchModal({ open, summary, onClose }) {
  const { roles } = useContext(UserContext)
  const { selectedCompany } = useSelector((state) => state.companyList || {})
  const companyId = selectedCompany?.id || selectedCompany?._id
  const { executeMutation: executeUnlockBlocking, fetching: unlocking } =
    UnlockStatementsBlockingUnmatch()
  const [unlockingLocal, setUnlockingLocal] = useState(false)
  const isUnlocking = unlocking || unlockingLocal

  const isClosedPeriod = summary?.blockReason === 'CLOSED_PERIOD'
  const participants =
    Array.isArray(summary?.participants) && summary.participants.length > 0
      ? summary.participants
      : summary
        ? [{ cardLabel: summary.cardLabel, periodKey: summary.periodKey }]
        : []
  const labels = participants.map(participantLabel).filter(Boolean)
  const label = labels[0] || [summary?.cardLabel, summary?.periodKey].filter(Boolean).join(' — ')
  const many = labels.length > 1
  const closedLabel = summary?.closedPeriodLabel || label
  const lockedIds = useMemo(() => lockedStatementIdsFromSummary(summary), [summary])
  const roleList = Array.isArray(roles) ? roles : roles ? [roles] : []
  const isAdmin = roleList.includes(Roles.ADMIN)
  const showUnlockAll = !isClosedPeriod && lockedIds.length > 0 && Boolean(companyId)

  const handleUnlockAll = async () => {
    if (!showUnlockAll || isUnlocking) return
    if (!isAdmin) {
      toast.error(dt('reconcile.statementLinkedUnmatchUnlockAllForbidden'))
      return
    }
    setUnlockingLocal(true)
    try {
      const result = await executeUnlockBlocking({ companyId, statementIds: lockedIds })
      if (result.error) {
        toast.error(result.error.message || dt('reconcile.statementLinkedUnmatchUnlockAllError'))
        return
      }
      toast.success(dt('reconcile.statementLinkedUnmatchUnlockAllSuccess'))
      onClose?.()
    } finally {
      setUnlockingLocal(false)
    }
  }

  return (
    <CustomModal
      open={Boolean(open && summary)}
      onClose={isUnlocking ? undefined : onClose}
      title={dt('reconcile.statementLinkedUnmatchTitle')}
      size="small"
      dimmer="blurring"
      classNameModal="delete-obligation-modal"
      classNameContent="delete-obligation-modal-content"
      closeOnDimmerClick={!isUnlocking}
      closeOnEscape={!isUnlocking}
      content={
        <div className="delete-obligation-modal-body">
          <div className="delete-obligation-modal-icon" aria-hidden>
            <FeatherIcon icon="info" size={22} />
          </div>
          <p className="delete-obligation-modal-lead">
            {isClosedPeriod
              ? dt('reconcile.statementLinkedUnmatchClosedPeriod', { label: closedLabel })
              : many
                ? dt('reconcile.statementLinkedUnmatchBodyMany', { labels: labels.join(', ') })
                : dt('reconcile.statementLinkedUnmatchBody', { label })}
          </p>
          {!isClosedPeriod && many ? (
            <ul className="delete-obligation-modal-summary">
              {participants.map((item, idx) => (
                <li key={`${item.periodKey || 'p'}-${idx}`}>
                  {participantLabel(item)}
                  {item.isLocked === false ? ` (${dt('reconcile.statementLinkedUnmatchUnlocked')})` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      }
      customActions={
        <CustomModalActions
          left={
            showUnlockAll ? (
              <ScfoButton
                type="button"
                variant="ghost"
                loading={isUnlocking}
                disabled={isUnlocking}
                onClick={handleUnlockAll}
              >
                {dt('reconcile.statementLinkedUnmatchUnlockAll')}
              </ScfoButton>
            ) : null
          }
          confirmLabel={dt('common.close')}
          onConfirm={onClose}
          confirmDisabled={isUnlocking}
          confirmVariant="primary"
          confirmAsync={false}
        />
      }
    />
  )
}
